/**
 * useUsageEnforcement Hook
 *
 * React hook for checking and enforcing workspace usage limits.
 * Prevents actions that would exceed subscription plan limits.
 *
 * Usage:
 * ```tsx
 * const { canCreateTable, canAddTeamMember, showLimitDialog } = useUsageEnforcement(workspaceId);
 *
 * const handleCreateTable = async () => {
 *   const { allowed, reason } = await canCreateTable();
 *   if (!allowed) {
 *     showLimitDialog('tables', reason);
 *     return;
 *   }
 *   // Proceed with creation
 * };
 * ```
 */

import { useQuery } from '@tanstack/react-query';

import { useSubscription } from '@/hooks/useSubscription';
import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';

export type LimitType = 'tables' | 'team_members' | 'storage' | 'spreadsheet_pulls';

export interface LimitCheckResult {
  allowed: boolean;
  reason?: string;
  current?: number;
  limit?: number | null;
  /** True if data is still loading - should show loading state, not paywall */
  isLoading?: boolean;
}

/**
 * Usage enforcement hook for workspace limits
 */
export function useUsageEnforcement(workspaceId: string) {
  const rpc = useRpc();
  const { limits, showUpgradeDialog } = useSubscription(workspaceId);

  // Fetch current workspace with usage stats
  const { data: workspaceData } = useQuery({
    queryKey: queryKeys.workspace.current(workspaceId),
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.me.$get();
      if (!res.ok) {
        return null;
      }
      const response = await res.json();
      // Check for error response
      if ('error' in response) {
        return null;
      }
      // Backend returns { workspace, membership, user, storage } directly
      return response;
    },
    enabled: !!workspaceId,
  });

  // Fetch tables for counting
  const { data: tablesData } = useQuery({
    queryKey: queryKeys.tables.list({ workspaceId }),
    queryFn: async () => {
      const res = await rpc.api.private.tables.$get({
        query: {}
      });
      if (!res.ok) {
        return [];
      }
      // Tables response: { data: [...], pagination: {...} }
      const response = await res.json();
      return response.data ?? [];
    },
    enabled: !!workspaceId,
  });

  // Note: Team members count is fetched via /workspaces/me (workspaceData.members)
  // We don't fetch /members directly because it requires owner role

  /**
   * Check if workspace can create a new table
   */
  const canCreateTable = (): LimitCheckResult => {
    if (!limits || !tablesData) {
      return { allowed: false, reason: 'Loading subscription data...', isLoading: true };
    }

    const currentCount = tablesData.length;
    const limit = limits.max_tables;

    // null = unlimited
    if (limit === null) {
      return { allowed: true };
    }

    // 0 = subscription expired or no plan (restrictive limit)
    // Return allowed: false without current/limit to trigger subscription dialog
    if (limit === 0) {
      return {
        allowed: false,
        reason: 'Your subscription has expired or no plan is active.',
      };
    }

    if (currentCount >= limit) {
      return {
        allowed: false,
        reason: `Your plan allows up to ${limit} tables. You currently have ${currentCount}.`,
        current: currentCount,
        limit,
      };
    }

    return { allowed: true, current: currentCount, limit };
  };

  /**
   * Check if workspace can add a new team member
   * Uses members count from /workspaces/me endpoint (no separate /members call needed)
   */
  const canAddTeamMember = (): LimitCheckResult => {
    if (!limits || !workspaceData?.members) {
      return { allowed: false, reason: 'Loading subscription data...', isLoading: true };
    }

    const currentCount = workspaceData.members.usage;
    const limit = workspaceData.members.limit;

    // null = unlimited
    if (limit === null) {
      return { allowed: true };
    }

    if (currentCount >= limit) {
      return {
        allowed: false,
        reason: `Your plan allows up to ${limit} team members. You currently have ${currentCount}.`,
        current: currentCount,
        limit,
      };
    }

    return { allowed: true, current: currentCount, limit };
  };

  /**
   * Check if workspace has storage available for version creation
   */
  const canVersionFile = (fileSizeBytes?: number): LimitCheckResult => {
    if (!workspaceData?.storage) {
      return { allowed: false, reason: 'Loading storage data...' };
    }

    const { usage, limit, available } = workspaceData.storage;

    // null = unlimited
    if (limit === null) {
      return { allowed: true };
    }

    // Check if any storage is available
    if (available !== null && available <= 0) {
      return {
        allowed: false,
        reason: 'Storage limit reached. Upgrade your plan to get more storage.',
        current: usage,
        limit,
      };
    }

    // If file size is provided, check if it fits
    if (fileSizeBytes && available !== null && fileSizeBytes > available) {
      const fileSizeMB = (fileSizeBytes / 1024 / 1024).toFixed(2);
      const availableMB = (available / 1024 / 1024).toFixed(2);
      return {
        allowed: false,
        reason: `File size (${fileSizeMB} MB) exceeds available storage (${availableMB} MB).`,
        current: usage,
        limit,
      };
    }

    return { allowed: true, current: usage, limit };
  };

  /**
   * Show upgrade dialog with context about the limit reached
   */
  const showLimitDialog = (limitType: LimitType, message?: string) => {
    const contextMessages: Record<LimitType, string> = {
      tables: message || 'Upgrade your plan to create more tables',
      team_members: message || 'Upgrade your plan to invite more team members',
      storage: message || 'Upgrade your plan to get more storage',
      spreadsheet_pulls: message || 'Upgrade your plan to get more spreadsheet pulls',
    };

    showUpgradeDialog({
      type: 'feature_access',
      message: contextMessages[limitType],
    });
  };

  /**
   * Get current usage summary
   */
  const getUsageSummary = () => {
    return {
      tables: {
        current: tablesData?.length ?? 0,
        limit: limits?.max_tables ?? null,
        percentage: limits?.max_tables
          ? ((tablesData?.length ?? 0) / limits.max_tables) * 100
          : 0,
      },
      teamMembers: workspaceData?.members
        ? {
            current: workspaceData.members.usage,
            limit: workspaceData.members.limit,
            percentage: workspaceData.members.percent_used,
          }
        : null,
      storage: workspaceData?.storage
        ? {
            current: workspaceData.storage.usage,
            limit: workspaceData.storage.limit,
            percentage: workspaceData.storage.percent_used,
          }
        : null,
    };
  };

  return {
    // Limit checks
    canCreateTable,
    canAddTeamMember,
    canVersionFile,

    // Usage summary
    getUsageSummary,

    // UI helpers
    showLimitDialog,

    // Raw data
    limits,
    workspaceData,
    tablesData,
  };
}
